import React, { useContext } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import { getApplicationsCount } from "../../utils/applicationsTypesCounter";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorIcon from "@mui/icons-material/Error";
import { UserDetailsContext } from "../../Context/userDetailsContext";
import { ConfigLinksDetailsContext } from "../../Context/cofingLinksDetailsContext";
import { useTranslation } from "react-i18next";

const DataBoard = ({ appicationsData }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { userDetails } = useContext(UserDetailsContext);
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const hideCancelledRequests = configLinks?.hideCancelledRequests ?? false;
  const { t } = useTranslation();

  const statusMap = {
    1: { name: t('status_new'), slug: "new" },
    2: { name: t('status_inProgress'), slug: "in-progress" },
    3: { name: t('status_pending'), slug: "pending" },
    4: { name: t('status_completed'), slug: "completed" },
    5: { name: t('status_rejected'), slug: "rejected" },
    6: { name: t('status_cancelled'), slug: "canceled" },
  };

  const handleClick = (code) => {
    if (code) {
      navigate(`/my-orders/${code}`);
    } else {
      navigate(`/my-orders`);
    }
  };

  // Calculate statistics
  const canceledOrders = getApplicationsCount(appicationsData, 6);
  const totalOrders = hideCancelledRequests ? appicationsData.length - canceledOrders : appicationsData.length;
  const newOrders = getApplicationsCount(appicationsData, 1);
  const inProgressOrders = getApplicationsCount(appicationsData, 2);
  const pendingOrders = getApplicationsCount(appicationsData, 3);
  const completedOrders = getApplicationsCount(appicationsData, 4);
  const rejectedOrders = getApplicationsCount(appicationsData, 5);
  const completionRate = totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0;

  // Get recent orders (last 2), filter out cancelled if setting is enabled
  const filteredOrders = hideCancelledRequests 
    ? appicationsData.filter(order => order.status?.id !== 6)
    : appicationsData;
  const recentOrders = filteredOrders.slice(0, 2);

  // Status configurations with icons and colors
  const statusConfigs = [
    {
      id: 1,
      title: t('status_new'),
      count: newOrders,
      icon: FiberNewIcon,
      color: theme.palette.primary.main,
      slug: "new",
    },
    {
      id: 2,
      title: t('status_inProgress'),
      count: inProgressOrders,
      icon: AccessTimeIcon,
      color: theme.palette.warning.main,
      slug: "in-progress",
    },
    {
      id: 3,
      title: t('status_pending'),
      count: pendingOrders,
      icon: InfoIcon,
      color: theme.palette.info.main,
      slug: "pending",
    },
    {
      id: 4,
      title: t('status_completed'),
      count: completedOrders,
      icon: CheckCircleIcon,
      color: theme.palette.success.main,
      slug: "completed",
    },
    {
      id: 5,
      title: t('status_rejected'),
      count: rejectedOrders,
      icon: ErrorIcon,
      color: theme.palette.error.main,
      slug: "rejected",
    },
    {
      id: 6,
      title: t('status_cancelled'),
      count: canceledOrders,
      icon: CancelIcon,
      color: "#757575",
      slug: "canceled",
    },
  ];

  return (
    <Box>
      {/* Welcome Section */}
      <Paper
        elevation={3}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          p: 4,
          mb: 3,
          borderRadius: "16px",
        }}
      >
        <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems="center" gap={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              {t('dashboard_welcome', { name: userDetails?.citizen?.fullName || t('dashboard_user') })}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
              {t('dashboard_ordersSummary', { total: totalOrders, inProgress: inProgressOrders })}
            </Typography>
            <Button
              variant="contained"
              sx={{
                borderRadius: "13px",
                backgroundColor: "white",
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.9)",
                },
              }}
              onClick={() => navigate("/services")}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <AddCircleOutlineIcon />
                <Typography>{t('dashboard_requestService')}</Typography>
              </Stack>
            </Button>
          </Box>
          <Box sx={{ textAlign: "center", minWidth: 120 }}>
            <Typography variant="h2" sx={{ fontWeight: "bold" }}>
              {completionRate}%
            </Typography>
            <Typography variant="body2">{t('dashboard_completionRate')}</Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Status Statistics - All 6 Types */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statusConfigs.filter(status => !(hideCancelledRequests && status.id === 6)).map((status) => {
          const IconComponent = status.icon;
          return (
            <Grid item xs={6} sm={4} md={2} key={status.id}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: status.color,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: `0 8px 20px ${status.color}80`,
                  },
                }}
                onClick={() => navigate(`/my-orders/${status.slug}`)}
              >
                <Stack alignItems="center" justifyContent="center" gap={1.5} sx={{ width: "100%" }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" gap={1.5}>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "white" }}>
                      {status.count}
                    </Typography>
                    <IconComponent sx={{ fontSize: 32, color: "white" }} />
                  </Stack>
                  <Typography sx={{ textAlign: "center", fontWeight: "bold", color: "white", fontSize: "1.35rem" }}>
                    {status.title}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Recent Activity Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: "12px", height: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {t('dashboard_recentOrders')}
              </Typography>
              {totalOrders > 0 && (
                <Button
                  size="small"
                  endIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/my-orders")}
                  sx={{ gap: 1, px: 1 }}
                >
                  {t('dashboard_viewAll')}
                </Button>
              )}
            </Stack>
            {recentOrders.length > 0 ? (
              <Stack gap={2}>
                {recentOrders.map((order, index) => (
                  <Paper
                    key={index}
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: "8px",
                      border: "1px solid",
                      borderColor: "divider",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        transform: "translateX(-5px)",
                      },
                    }}
                    onClick={() => navigate(`/order-details/${order.subscriberServiceId}`)}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" gap={1.5} alignItems="center">
                        <DescriptionIcon color="primary" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {order.serviceName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard_orderNumber', { number: order.subscriberServiceFullNumber })}
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        label={statusMap[order.status?.id]?.name || "غير محدد"}
                        size="small"
                        color={
                          order.status?.id === 4
                            ? "success"
                            : order.status?.id === 2
                            ? "warning"
                            : order.status?.id === 5
                            ? "error"
                            : "default"
                        }
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <DescriptionIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard_noOrders')}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate("/services")}
                  sx={{ width: "100%", mt: 2 }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <AddCircleOutlineIcon />
                    <Typography>{t('dashboard_requestService')}</Typography>
                  </Stack>
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: "12px", height: "100%" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              {t('dashboard_quickLinks')}
            </Typography>
            <Stack gap={1.5}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                onClick={() => navigate("/services")}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%" }}>
                  <AddCircleOutlineIcon />
                  <Typography>{t('dashboard_requestService')}</Typography>
                </Stack>
              </Button>
              <Button
                variant="outlined"
                fullWidth
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                onClick={() => navigate("/services-guide")}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%" }}>
                  <DescriptionIcon />
                  <Typography>{t('dashboard_servicesGuide')}</Typography>
                </Stack>
              </Button>
              <Button
                variant="outlined"
                fullWidth
                sx={{ justifyContent: "flex-start", py: 1.5 }}
                onClick={() => navigate("/my-notifications")}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%" }}>
                  <NotificationsActiveIcon />
                  <Typography>{t('dashboard_notifications')}</Typography>
                </Stack>
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataBoard;
